package com.swetdot.swetgram.ui.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.swetdot.swetgram.data.moderation.ReportTarget
import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.domain.ProfileVisibility
import com.swetdot.swetgram.domain.UserProfile
import com.swetdot.swetgram.ui.chat.DayLabel
import com.swetdot.swetgram.ui.chat.dayLabel
import com.swetdot.swetgram.ui.chat.formatClockTime
import com.swetdot.swetgram.ui.chat.render
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetContentPhase
import com.swetdot.swetgram.ui.design.SwetStateHost
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.swetContentPhase
import com.swetdot.swetgram.ui.moderation.ReportRequest
import com.swetdot.swetgram.ui.moderation.ReportedAccount
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.text.resolveOrNull
import com.swetdot.swetgram.ui.text.text
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.handle_prefixed
import swetgram.shared.generated.resources.profile_block
import swetgram.shared.generated.resources.profile_block_body
import swetgram.shared.generated.resources.profile_block_title
import swetgram.shared.generated.resources.profile_edit_bio_label
import swetgram.shared.generated.resources.profile_label_birthday
import swetgram.shared.generated.resources.profile_label_location
import swetgram.shared.generated.resources.profile_label_username
import swetgram.shared.generated.resources.profile_media_photo_count
import swetgram.shared.generated.resources.profile_message
import swetgram.shared.generated.resources.profile_presence_last_seen_on
import swetgram.shared.generated.resources.profile_presence_last_seen_today
import swetgram.shared.generated.resources.profile_presence_last_seen_yesterday
import swetgram.shared.generated.resources.profile_presence_offline
import swetgram.shared.generated.resources.profile_presence_online
import swetgram.shared.generated.resources.profile_public_back
import swetgram.shared.generated.resources.profile_public_blocked_by_me
import swetgram.shared.generated.resources.profile_public_limited
import swetgram.shared.generated.resources.profile_public_more_actions
import swetgram.shared.generated.resources.profile_public_show_qr
import swetgram.shared.generated.resources.profile_report
import swetgram.shared.generated.resources.profile_tab_media
import swetgram.shared.generated.resources.profile_title
import swetgram.shared.generated.resources.profile_try_again
import swetgram.shared.generated.resources.profile_unavailable_body_theirs
import swetgram.shared.generated.resources.profile_unavailable_title
import swetgram.shared.generated.resources.profile_unblock
import swetgram.shared.generated.resources.profile_unblock_body
import swetgram.shared.generated.resources.profile_unblock_title
import kotlin.time.Clock
import kotlin.time.Instant

/** Public-profile presentation; state and actions remain owned by [PublicProfileViewModel]. */
@Composable
fun PublicProfileScreen(
    state: PublicProfileUiState,
    onBack: () -> Unit,
    onMessage: () -> Unit,
    onBlock: () -> Unit,
    onUnblock: () -> Unit,
    onShowQr: () -> Unit,
    onDismissQr: () -> Unit,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
    onReport: ((ReportRequest) -> Unit)? = null,
    now: Instant = Clock.System.now(),
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    var confirming by remember { mutableStateOf<PublicProfileConfirmation?>(null) }
    var topMenuExpanded by remember { mutableStateOf(false) }
    var actionMenuExpanded by remember { mutableStateOf(false) }
    val profile = state.profile

    val reportAction: (() -> Unit)? = if (profile != null && onReport != null) {
        {
            onReport(
                ReportRequest(
                    target = ReportTarget.Person(profile.user.id),
                    account = ReportedAccount(
                        userId = profile.user.id,
                        username = profile.user.username,
                        displayName = profile.user.displayName,
                    ),
                ),
            )
        }
    } else {
        null
    }
    val blocked = profile?.isBlockedByMe == true
    val blockConfirmation = if (blocked) PublicProfileConfirmation.Unblock else PublicProfileConfirmation.Block
    val blockLabel = if (blocked) {
        stringResource(Res.string.profile_unblock)
    } else {
        stringResource(Res.string.profile_block)
    }

    Box(modifier.fillMaxSize().background(SwetgramThemeTokens.colors.backgroundDeep)) {
        Column(Modifier.fillMaxSize()) {
            PublicProfileTopBar(
                title = stringResource(Res.string.profile_title),
                backLabel = stringResource(Res.string.profile_public_back),
                moreLabel = stringResource(Res.string.profile_public_more_actions),
                showMore = profile != null,
                menuExpanded = topMenuExpanded,
                onBack = onBack,
                onMore = { topMenuExpanded = true },
                onDismissMenu = { topMenuExpanded = false },
            ) {
                PublicProfileOverflowItems(
                    reportAction = reportAction,
                    reportLabel = stringResource(Res.string.profile_report),
                    blockLabel = blockLabel,
                    blocked = blocked,
                    enabled = !state.busy,
                    onBlock = { confirming = blockConfirmation },
                    onDismiss = { topMenuExpanded = false },
                )
            }

            if (state.error != null) {
                SwetErrorBanner(
                    message = state.error.resolve(),
                    onRetry = onRetry,
                    onDismiss = onDismissError,
                )
            }

            SwetStateHost(
                phase = swetContentPhase(
                    hasContent = profile != null,
                    loadedOnce = state.loadedOnce,
                    hasError = state.error != null,
                ),
                modifier = Modifier.fillMaxSize(),
            ) { phase -> when (phase) {
                SwetContentPhase.Loading -> ModernPublicProfileSkeleton()

                SwetContentPhase.Empty,
                SwetContentPhase.Error -> SwetEmptyState(
                    title = stringResource(Res.string.profile_unavailable_title),
                    body = stringResource(Res.string.profile_unavailable_body_theirs),
                    actionText = if (onRetry != null) stringResource(Res.string.profile_try_again) else null,
                    onAction = onRetry,
                )

                SwetContentPhase.Content -> if (profile != null) BoxWithConstraints(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.TopCenter,
                ) {
                    Column(
                        modifier = Modifier
                            .widthIn(max = PublicProfileMaxWidth)
                            .fillMaxWidth()
                            .fillMaxSize()
                            .verticalScroll(rememberScrollState())
                            .navigationBarsPadding()
                            .padding(horizontal = PublicProfileMargin),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        Spacer(Modifier.height(Space.one))
                        PublicProfileHero(
                            displayName = profile.user.displayName,
                            username = profile.user.username,
                            bio = profile.user.bio,
                            avatarUrl = profile.user.avatarUrl,
                            presenceLine = presenceLine(profile, now, zone).resolveOrNull(),
                            isOnline = profile.isOnline == true,
                            hasBlackBadge = profile.user.hasBlackBadge,
                        )

                        if (profile.visibility == ProfileVisibility.LIMITED) {
                            Spacer(Modifier.height(Space.half))
                            ProfileSupportingText(stringResource(Res.string.profile_public_limited))
                        }
                        if (profile.isBlockedByMe) {
                            Spacer(Modifier.height(Space.half))
                            ProfileSupportingText(stringResource(Res.string.profile_public_blocked_by_me))
                        }

                        Spacer(Modifier.height(Space.two))
                        ProfileActionRow(
                            primaryText = if (profile.isBlockedByMe) blockLabel else {
                                stringResource(Res.string.profile_message)
                            },
                            primaryIcon = if (profile.isBlockedByMe) SwetIcons.Ban else SwetIcons.MessageCircle,
                            primaryEnabled = !state.busy,
                            primaryLoading = state.opening,
                            moreLabel = stringResource(Res.string.profile_public_more_actions),
                            menuExpanded = actionMenuExpanded,
                            showMore = true,
                            onPrimary = if (profile.isBlockedByMe) {
                                { confirming = PublicProfileConfirmation.Unblock }
                            } else {
                                onMessage
                            },
                            onMore = { actionMenuExpanded = true },
                            onDismissMenu = { actionMenuExpanded = false },
                        ) {
                            PublicProfileOverflowItems(
                                reportAction = reportAction,
                                reportLabel = stringResource(Res.string.profile_report),
                                blockLabel = blockLabel,
                                blocked = profile.isBlockedByMe,
                                enabled = !state.busy,
                                onBlock = { confirming = blockConfirmation },
                                onDismiss = { actionMenuExpanded = false },
                            )
                        }

                        Spacer(Modifier.height(Space.one + Space.half))
                        ProfileInfoCard(
                            icon = null,
                            leadingText = "@",
                            title = stringResource(Res.string.profile_label_username),
                            value = stringResource(Res.string.handle_prefixed, profile.user.username),
                            trailingIcon = SwetIcons.QrCode,
                            trailingLabel = stringResource(Res.string.profile_public_show_qr),
                            onTrailingClick = onShowQr,
                        )

                        val birthday = remember(profile.user.dateOfBirth, profile.user.showBirthYear) {
                            formatProfileBirthday(profile.user.dateOfBirth, profile.user.showBirthYear)
                        }
                        if (birthday != null) {
                            Spacer(Modifier.height(Space.one))
                            ProfileInfoCard(
                                icon = SwetIcons.Calendar,
                                title = stringResource(Res.string.profile_label_birthday),
                                value = birthday,
                                labelFirst = true,
                            )
                        }

                        val location = profile.user.location?.trim()?.takeIf { it.isNotEmpty() }
                        val mediaCount = profile.media.size.takeIf { it > 0 }
                        if (location != null || mediaCount != null) {
                            Spacer(Modifier.height(Space.one))
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(Space.one),
                            ) {
                                if (location != null) {
                                    ProfileInfoCard(
                                        icon = SwetIcons.MapPin,
                                        title = stringResource(Res.string.profile_label_location),
                                        value = location,
                                        labelFirst = true,
                                        modifier = Modifier.weight(1f),
                                    )
                                }
                                if (mediaCount != null) {
                                    ProfileInfoCard(
                                        icon = SwetIcons.ImageIcon,
                                        title = stringResource(Res.string.profile_tab_media),
                                        value = pluralStringResource(
                                            Res.plurals.profile_media_photo_count,
                                            mediaCount,
                                            mediaCount,
                                        ),
                                        labelFirst = true,
                                        modifier = Modifier.weight(1f),
                                    )
                                }
                            }
                        }

                        Spacer(Modifier.height(Space.one + Space.half))
                        ProfileSafetyActions(
                            reportText = if (reportAction != null) {
                                stringResource(Res.string.profile_report)
                            } else {
                                null
                            },
                            blockText = blockLabel,
                            blockIcon = if (profile.isBlockedByMe) SwetIcons.Refresh else SwetIcons.Ban,
                            enabled = !state.busy,
                            onReport = reportAction,
                            onBlock = { confirming = blockConfirmation },
                        )
                        Spacer(Modifier.height(Space.one))
                    }
                }
            } }
        }
    }

    when (confirming) {
        null -> Unit
        PublicProfileConfirmation.Block -> ProfileConfirmDialog(
            title = stringResource(Res.string.profile_block_title, profile?.user?.displayName.orEmpty()),
            body = stringResource(Res.string.profile_block_body),
            confirmText = stringResource(Res.string.profile_block),
            onConfirm = {
                confirming = null
                onBlock()
            },
            onDismiss = { confirming = null },
            busy = state.busy,
        )
        PublicProfileConfirmation.Unblock -> ProfileConfirmDialog(
            title = stringResource(Res.string.profile_unblock_title, profile?.user?.displayName.orEmpty()),
            body = stringResource(Res.string.profile_unblock_body),
            confirmText = stringResource(Res.string.profile_unblock),
            onConfirm = {
                confirming = null
                onUnblock()
            },
            onDismiss = { confirming = null },
            destructive = false,
            busy = state.busy,
        )
    }

    if (state.qr.visible) {
        QrDialog(state = state.qr, onDismiss = onDismissQr)
    }
}

@Composable
private fun ProfileSupportingText(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall,
        color = SwetgramThemeTokens.colors.textSecondary,
        textAlign = TextAlign.Center,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun PublicProfileOverflowItems(
    reportAction: (() -> Unit)?,
    reportLabel: String,
    blockLabel: String,
    blocked: Boolean,
    enabled: Boolean,
    onBlock: () -> Unit,
    onDismiss: () -> Unit,
) {
    if (reportAction != null) {
        PublicProfileMenuItem(
            icon = SwetIcons.Flag,
            text = reportLabel,
            enabled = enabled,
            onClick = {
                onDismiss()
                reportAction()
            },
        )
    }
    PublicProfileMenuItem(
        icon = if (blocked) SwetIcons.Refresh else SwetIcons.Ban,
        text = blockLabel,
        enabled = enabled,
        onClick = {
            onDismiss()
            onBlock()
        },
    )
}

private enum class PublicProfileConfirmation { Block, Unblock }

internal fun formatProfileBirthday(raw: String?, showBirthYear: Boolean): String? {
    val date = raw?.let { runCatching { LocalDate.parse(it) }.getOrNull() } ?: return null
    val month = date.month.ordinal + 1
    val dayMonth = "${date.day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}"
    return if (showBirthYear && date.year > 0) "$dayMonth.${date.year}" else dayMonth
}

internal fun presenceLine(profile: UserProfile, now: Instant, zone: TimeZone): UiText? {
    if (profile.isOnline == true) return Res.string.profile_presence_online.text()
    val lastSeen = profile.lastSeenAt
        ?: return if (profile.isOnline == false) Res.string.profile_presence_offline.text() else null
    val clock = formatClockTime(lastSeen, zone)
    return when (val label = dayLabel(LocalDay.of(lastSeen, zone), now, zone)) {
        DayLabel.Today -> Res.string.profile_presence_last_seen_today.text(clock)
        DayLabel.Yesterday -> Res.string.profile_presence_last_seen_yesterday.text(clock)
        is DayLabel.Weekday -> Res.string.profile_presence_last_seen_on.text(label.day.render())
        is DayLabel.Absolute -> Res.string.profile_presence_last_seen_on.text(label.day.render())
    }
}
